import { useState, useEffect, useMemo } from "react";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Cell,
} from "recharts";

// --- PERBAIKAN 1: Pastikan huruf besar 'P' sesuai nama file asli di foldermu ---
import Pempeng from "../btn-assets/pempeng";

const Pembukuan = () => {
  // --- STATE ---
  const [filterBulan, setFilterBulan] = useState("");
  const [filterTahun, setFilterTahun] = useState("");
  const [tampil, setTampil] = useState(false);
  const [hal, setHal] = useState("Kosong");

  // State Data
  const [trs, setTrs] = useState(0);
  const [masuk, setMasuk] = useState(0);
  const [keluar, setKeluar] = useState(0);
  const [data, setData] = useState([]);

  // State Debugging
  const [debugError, setDebugError] = useState("");
  const [isLoading, setIsLoading] = useState(false);

  // --- LOGIKA HITUNGAN ---
  const sisa = Number(trs) + Number(masuk) - Number(keluar);

  const chartData = useMemo(() => {
    const totalPemasukan = Number(trs) + Number(masuk);
    const totalPengeluaran = Number(keluar);
    const sisaSaldo = totalPemasukan - totalPengeluaran;

    // Log ke console untuk memastikan data masuk ke chart
    console.log("Data Chart:", { totalPemasukan, totalPengeluaran, sisaSaldo });

    return [
      { name: "Pemasukan", value: totalPemasukan, color: "#10b981" },
      { name: "Pengeluaran", value: totalPengeluaran, color: "#ef4444" },
      { name: "Sisa Saldo", value: sisaSaldo, color: "#3b82f6" },
    ];
  }, [trs, masuk, keluar]);

  // --- FETCH DATA ---
  const fetchData = async () => {
    setIsLoading(true);
    setDebugError("");

    const query =
      filterBulan && filterTahun
        ? `?bulan=${filterBulan}&tahun=${filterTahun}`
        : "";

    try {
      // 1. Ambil Data Tabel
      const res = await fetch(
        `https://api.ecotrace.id/api/get-pembukuan${query}`,
      );
      if (!res.ok) throw new Error(`Gagal Tabel: HTTP ${res.status}`);
      const temp = await res.json();
      if (temp.succeed) setData(temp.data);

      // 2. Ambil Data Saldo
      const res2 = await fetch(
        `https://api.ecotrace.id/api/get-informasi-all${query}`,
      );
      if (!res2.ok) throw new Error(`Gagal Saldo: HTTP ${res2.status}`);
      const temp2 = await res2.json();

      if (temp2.succeed) {
        setTrs(temp2.trs);
        setMasuk(temp2.masuk);
        setKeluar(temp2.keluar);
      }
    } catch (error) {
      console.error("Error:", error);
      setDebugError(error.message); // Munculkan error di layar HP
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [filterBulan, filterTahun]);

  const daftarBulan = [
    "Januari",
    "Februari",
    "Maret",
    "April",
    "Mei",
    "Juni",
    "Juli",
    "Agustus",
    "September",
    "Oktober",
    "November",
    "Desember",
  ];

  return (
    <div className="w-full font-sans text-gray-700 px-2 sm:px-0 pb-10">
      {/* Pop-up Form */}
      {tampil && <Pempeng setTampil={setTampil} hal={hal} />}

      {/* --- ERROR MESSAGE AREA (Supaya kita tahu kenapa error) --- */}
      <div className="w-full px-2 mb-2">
        {isLoading && (
          <p className="text-blue-500 text-xs animate-pulse">
            Sedang memuat data...
          </p>
        )}
        {debugError && (
          <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded relative text-sm">
            <strong className="font-bold">Error System: </strong> {debugError}
            <br />
            <span className="text-xs">
              Cek Console Log browser untuk detailnya.
            </span>
          </div>
        )}
      </div>

      {/* Bagian Filter & Tombol */}
      <div className="flex flex-wrap gap-3 mb-6 items-center">
        <select
          className="px-4 py-2 bg-white border border-gray-300 rounded-full text-sm outline-none"
          value={filterBulan}
          onChange={(e) => setFilterBulan(e.target.value)}
        >
          <option value="">Pilih Bulan</option>
          {daftarBulan.map((b) => (
            <option key={b} value={b}>
              {b}
            </option>
          ))}
        </select>

        <select
          className="px-4 py-2 bg-white border border-gray-300 rounded-full text-sm outline-none"
          value={filterTahun}
          onChange={(e) => setFilterTahun(e.target.value)}
        >
          <option value="">Pilih Tahun</option>
          <option value="2024">2024</option>
          <option value="2025">2025</option>
          <option value="2026">2026</option>
        </select>

        {(filterBulan || filterTahun) && (
          <button
            onClick={() => {
              setFilterBulan("");
              setFilterTahun("");
            }}
            className="text-red-500 text-xs hover:underline"
          >
            Reset Filter
          </button>
        )}

        {/* --- TOMBOL YANG HILANG --- */}
        <div className="flex gap-2 ml-auto">
          <button
            onClick={() => {
              setHal("Pemasukan");
              setTampil(true);
            }}
            className="px-4 py-2 bg-navBase text-white rounded-full text-xs font-medium hover:bg-blue-500 transition shadow-sm"
          >
            + Pemasukan
          </button>
          <button
            onClick={() => {
              setHal("Pengeluaran");
              setTampil(true);
            }}
            className="px-4 py-2 bg-navBase text-white rounded-full text-xs font-medium hover:bg-blue-500 transition shadow-sm"
          >
            + Pengeluaran
          </button>
        </div>
      </div>

      {/* Kartu Informasi Saldo */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 mb-8">
        <div className="bg-white p-5 rounded-2xl shadow-sm border border-gray-100 flex flex-col justify-center min-h-[100px]">
          <h3 className="text-gray-400 text-sm md:text-base mb-1">
            Total Pemasukan
          </h3>
          <h2 className="text-xl md:text-2xl font-bold text-green-600 truncate">
            {"Rp " + (Number(trs) + Number(masuk)).toLocaleString("id-ID")}
          </h2>
        </div>

        <div className="bg-white p-5 rounded-2xl shadow-sm border border-gray-100 flex flex-col justify-center min-h-[100px]">
          <h3 className="text-gray-400 text-sm md:text-base mb-1">
            Total Pengeluaran
          </h3>
          <h2 className="text-xl md:text-2xl font-bold text-red-500 truncate">
            {"Rp " + Number(keluar).toLocaleString("id-ID")}
          </h2>
        </div>

        <div className="bg-white p-5 rounded-2xl shadow-sm border border-gray-100 flex flex-col justify-center min-h-[100px] sm:col-span-2 lg:col-span-1">
          <h3 className="text-gray-400 text-sm md:text-base mb-1">
            Sisa Saldo
          </h3>
          <h2 className="text-xl md:text-2xl font-bold text-blue-600 truncate">
            {"Rp " + sisa.toLocaleString("id-ID")}
          </h2>
        </div>
      </div>

      {/* Grafik */}
      <div className="w-full bg-white p-4 md:p-6 rounded-2xl shadow-sm border border-gray-100 mb-8">
        <h3 className="text-sm font-bold text-gray-500 mb-4">
          Visualisasi Arus Kas
        </h3>
        <div className="h-64 md:h-80 w-full">
          {/* Cek apakah ada data non-nol agar grafik tidak gepeng */}
          {Number(trs) + Number(masuk) > 0 || Number(keluar) > 0 ? (
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={chartData}>
                <CartesianGrid
                  strokeDasharray="3 3"
                  vertical={false}
                  stroke="#f0f0f0"
                />
                <XAxis
                  dataKey="name"
                  axisLine={false}
                  tickLine={false}
                  tick={{ fill: "#9ca3af", fontSize: 12 }}
                />
                <YAxis
                  hide={window.innerWidth < 640}
                  axisLine={false}
                  tickLine={false}
                  tick={{ fill: "#9ca3af", fontSize: 12 }}
                />
                <Tooltip
                  cursor={{ fill: "#f9fafb" }}
                  content={({ active, payload }) => {
                    if (active && payload && payload.length) {
                      const d = payload[0].payload;
                      return (
                        <div className="bg-white p-3 rounded-lg shadow-lg border border-gray-100">
                          <p className="text-xs text-gray-400 mb-1">{d.name}</p>
                          <p className="font-bold text-sm">
                            Rp {d.value.toLocaleString("id-ID")}
                          </p>
                        </div>
                      );
                    }
                    return null;
                  }}
                />
                <Bar dataKey="value" radius={[6, 6, 0, 0]} barSize={40}>
                  {chartData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry.color} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          ) : (
            <div className="h-full flex flex-col items-center justify-center text-gray-400 italic">
              <p>Grafik kosong karena Saldo/Transaksi masih Rp 0</p>
            </div>
          )}
        </div>
      </div>

      {/* Tabel */}
      <div className="w-full bg-white rounded-lg shadow-sm border border-gray-100 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-left border-collapse min-w-[600px]">
            <thead className="bg-cyan-50/50">
              <tr>
                <th className="py-3 px-4 md:px-6 text-xs md:text-sm font-bold text-gray-700">
                  Tanggal
                </th>
                <th className="py-3 px-4 md:px-6 text-xs md:text-sm font-bold text-gray-700">
                  Nominal
                </th>
                <th className="py-3 px-4 md:px-6 text-xs md:text-sm font-bold text-gray-700">
                  Keterangan
                </th>
                <th className="py-3 px-4 md:px-6 text-xs md:text-sm font-bold text-gray-700 text-center">
                  Status
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {Array.isArray(data) && data.length > 0 ? (
                data.map((item, index) => (
                  <tr
                    key={index}
                    className="hover:bg-gray-50 transition-colors"
                  >
                    <td className="py-3 px-4 md:px-6 text-xs md:text-sm text-gray-600">
                      {item.bulan + " " + item.tahun}
                    </td>
                    <td className="py-3 px-4 md:px-6 text-xs md:text-sm font-bold text-gray-800">
                      {"Rp " + item.uang.toLocaleString("id-ID")}
                    </td>
                    <td className="py-3 px-4 md:px-6 text-xs md:text-sm text-gray-500 truncate max-w-[150px]">
                      {item.ket}
                    </td>
                    <td className="py-3 px-4 md:px-6 text-center">
                      <span
                        className={`px-3 py-1 rounded-full text-[10px] md:text-xs font-semibold ${
                          item.stat === "Pemasukan"
                            ? "bg-green-100 text-green-700"
                            : "bg-red-100 text-red-700"
                        }`}
                      >
                        {item.stat}
                      </span>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="4" className="text-center py-4 text-gray-400">
                    Belum ada data.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default Pembukuan;
